'use client';

import { Tag } from '@/components';
import { Column, Element, Icon, Row } from '@vuddy/components';
import { memo, useMemo } from 'react';
import type { ClassVoucher } from './classVoucherContext';
import { useClassVoucherSelection } from './classVoucherContext';
import * as styles from './classVoucherItem.css';

interface ClassVoucherItemProps {
  voucher: ClassVoucher;
  disabled?: boolean;
  hasVerificationCode?: boolean;
  verificationCodeName?: string;
  onCodeInputClick?: (voucherId: string) => void;
}

const formatPrice = (price: number) => `${price.toLocaleString()}원`;

export const ClassVoucherItem = memo(
  ({
    voucher,
    disabled,
    hasVerificationCode,
    verificationCodeName,
    onCodeInputClick,
  }: ClassVoucherItemProps) => {
    const { selectedIds, toggle } = useClassVoucherSelection();

    const isSelected = useMemo(
      () => selectedIds.includes(voucher.id),
      [selectedIds, voucher.id]
    );

    const handleClick = () => {
      if (disabled) return;
      toggle(voucher.id);
    };

    const highlightTone = voucher.highlightTone ?? 'primary';

    return (
      <Element
        as="button"
        type="button"
        className={styles.voucherItem({ selected: isSelected, disabled })}
        onClick={handleClick}
        disabled={disabled}
        aria-pressed={isSelected}>
        {(voucher.durationLabel || voucher.highlightLabel) && (
          <Element className={styles.chipsRow}>
            {voucher.durationLabel && (
              <Tag
                size="medium"
                variant="lightblue"
                iconName="OUTLINE_TIMER"
                label={voucher.durationLabel}
              />
            )}
            {voucher.highlightLabel && (
              <Tag
                size="medium"
                variant="lightred"
                label={voucher.highlightLabel}
              />
            )}
          </Element>
        )}

        <Element as="h3" className={styles.title}>
          {voucher.name}
        </Element>

        {hasVerificationCode && (
          <Row
            as="button"
            type="button"
            alignItems="center"
            gap="gap_2xs"
            onClick={e => {
              e.stopPropagation();
              onCodeInputClick?.(voucher.id);
            }}
            className={styles.verificationCodeButton}>
            <Icon name="FILL_PROMOTION_CODE" size="16px" color="secondary" />
            <Element
              typography="captionBaseAccent"
              color="secondary"
              className={
                verificationCodeName
                  ? styles.verificationCodeText
                  : styles.verificationCodeTextUnderline
              }>
              {verificationCodeName || '코드 입력'}
            </Element>
          </Row>
        )}

        <Element
          className={
            hasVerificationCode
              ? styles.dottedDividerWithCode
              : styles.dottedDivider
          }
          aria-hidden
        />

        <Element className={styles.priceSection}>
          <Element className={styles.priceColumn}>
            {voucher.normalPriceLabel && (
              <Element className={styles.smallLabel}>
                {voucher.normalPriceLabel}
              </Element>
            )}
            {voucher.discountPriceLabel && (
              <Element className={styles.smallValue}>
                {voucher.discountPriceLabel}
              </Element>
            )}
          </Element>
          <Row className={styles.discountColumn}>
            {voucher.discountBadgeLabel && (
              <Tag
                size="medium"
                variant="red"
                label={voucher.discountBadgeLabel}
              />
            )}
            <Column gap="gap_2xs">
              {voucher.subLabel && (
                <Element className={styles.subLabel}>
                  {voucher.subLabel}
                </Element>
              )}
              <Element className={styles.priceLabel}>
                {voucher.priceLabel ?? formatPrice(voucher.price)}
              </Element>
            </Column>
          </Row>
        </Element>
      </Element>
    );
  }
);

ClassVoucherItem.displayName = 'ClassVoucherItem';
